export type InvitationFormValues = {
  email: string;
  given_name: string;
  family_name: string;
  username: string;
  password: string;
  password_again: string;
};

export const hasUpperCase = (string: string) => string !== string.toLowerCase();
export const hasLowerCase = (string: string) => string !== string.toUpperCase();
export const hasNumbers = (string: string) => /\d/.test(string);

/**
 * Error keys returned by validateAccountForm. Each key corresponds to a
 * react-intl message id used in the account creation form.
 */
export const VALIDATION_ERROR = {
  NO_GIVEN_NAME: "error.validation.no_given_name",
  NO_FAMILY_NAME: "error.validation.no_family_name",
  NO_USERNAME: "error.validation.no_username",
  INVALID_USERNAME: "error.validation.no_valid_username",
  USERNAME_TOO_SHORT: "error.validation.username_too_short",
  USERNAME_TOO_LONG: "error.validation.username_too_long",
  NO_PASSWORD: "error.validation.no_password",
  PASSWORDS_DIFFERENT: "error.validation.passwords_different",
  PASSWORD_TOO_SHORT: "error.validation.password_too_short",
  PASSWORD_TOO_LONG: "error.validation.password_too_long",
  PASSWORD_UPPER_LOWER_DIGIT: "error.validation.password_upper_lower_digit",
  PASSWORD_SPACE: "error.validation.password_space",
} as const;

export type ValidationErrors = { [field: string]: string };

// A username can consist of letters, numbers, _, ".", +, -, or @.
const USERNAME_REGEX = /^[a-zA-Z0-9_.+\-@]+$/;

/**
 * Pure validation function for the account creation form.
 * Returns a map of field names to error message keys.
 * An empty object means the form is valid.
 */
export function validateAccountForm(values: InvitationFormValues): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!values.given_name) {
    errors.given_name = VALIDATION_ERROR.NO_GIVEN_NAME;
  }
  if (!values.family_name) {
    errors.family_name = VALIDATION_ERROR.NO_FAMILY_NAME;
  }

  if (!values.username) {
    errors.username = VALIDATION_ERROR.NO_USERNAME;
  } else if (!USERNAME_REGEX.test(values.username)) {
    errors.username = VALIDATION_ERROR.INVALID_USERNAME;
  } else if (values.username.length < 4) {
    errors.username = VALIDATION_ERROR.USERNAME_TOO_SHORT;
  } else if (values.username.length > 128) {
    errors.username = VALIDATION_ERROR.USERNAME_TOO_LONG;
  }

  const passwordString = String(values.password);
  const setBothPasswordErrors = (error: string) => {
    errors.password = error;
    errors.password_again = error;
  };

  if (!values.password) {
    errors.password = VALIDATION_ERROR.NO_PASSWORD;
  } else if (values.password !== values.password_again) {
    setBothPasswordErrors(VALIDATION_ERROR.PASSWORDS_DIFFERENT);
  } else if (passwordString.length < 8) {
    setBothPasswordErrors(VALIDATION_ERROR.PASSWORD_TOO_SHORT);
  } else if (passwordString.length > 99) {
    setBothPasswordErrors(VALIDATION_ERROR.PASSWORD_TOO_LONG);
  } else if (
    !hasUpperCase(passwordString) ||
    !hasLowerCase(passwordString) ||
    !hasNumbers(passwordString)
  ) {
    setBothPasswordErrors(VALIDATION_ERROR.PASSWORD_UPPER_LOWER_DIGIT);
  } else if (passwordString.trim() !== passwordString) {
    setBothPasswordErrors(VALIDATION_ERROR.PASSWORD_SPACE);
  }

  return errors;
}
